import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import KitchenLayout from '../Navigation/KitchenLayout';
import {
  createSecret,
  deleteSecret,
  getSecrets,
  SecretAccess,
  updateSecret,
} from '../../services/secretService';

const VaultPage: React.FC = () => {
  const [secrets, setSecrets] = useState<SecretAccess[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [newKind, setNewKind] = useState<'secret' | 'config'>('secret');
  const [newLabel, setNewLabel] = useState('');
  const [newService, setNewService] = useState('');
  const [newValue, setNewValue] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [replacingId, setReplacingId] = useState<string | null>(null);
  const [editKind, setEditKind] = useState<'secret' | 'config'>('secret');
  const [label, setLabel] = useState('');
  const [service, setService] = useState('');
  const [replacementValue, setReplacementValue] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState('');

  const loadSecrets = async () => {
    try {
      setSecrets(await getSecrets());
      setError('');
    } catch (requestError: any) {
      setError(requestError?.response?.data?.message || 'Could not load your vault');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadSecrets();
  }, []);

  const cancelAdd = () => {
    setIsAdding(false);
    setNewLabel('');
    setNewService('');
    setNewValue('');
    setNewKind('secret');
    setError('');
  };

  const addSecret = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!newLabel.trim() || !newValue.trim()) return;
    setIsCreating(true);
    setError('');
    try {
      await createSecret({
        label: newLabel,
        service: newService || null,
        value: newValue,
        kind: newKind,
      });
      setIsAdding(false);
      setNewLabel('');
      setNewService('');
      setNewValue('');
      setNewKind('secret');
      await loadSecrets();
    } catch (requestError: any) {
      setError(requestError?.response?.data?.message || 'Could not add this private information');
    } finally {
      setIsCreating(false);
    }
  };

  const beginEdit = (secret: SecretAccess) => {
    setEditingId(secret.id);
    setReplacingId(null);
    setLabel(secret.label);
    setEditKind(secret.kind);
    setService(secret.service || '');
    setError('');
  };

  const saveDetails = async (secretId: string) => {
    if (!label.trim()) return;
    setBusyId(secretId);
    try {
      await updateSecret(secretId, { label, service: service || null, kind: editKind });
      setEditingId(null);
      await loadSecrets();
    } catch (requestError: any) {
      setError(requestError?.response?.data?.message || 'Could not save these changes');
    } finally {
      setBusyId(null);
    }
  };

  const replaceValue = async (secretId: string) => {
    if (!replacementValue.trim()) return;
    setBusyId(secretId);
    try {
      await updateSecret(secretId, { value: replacementValue });
      setReplacementValue('');
      setReplacingId(null);
      await loadSecrets();
    } catch (requestError: any) {
      setError(requestError?.response?.data?.message || 'Could not replace the private value');
    } finally {
      setBusyId(null);
    }
  };

  const removeSecret = async (secret: SecretAccess) => {
    const recipeMessage = secret.recipeCount
      ? ` ${secret.recipeCount} recipe${secret.recipeCount === 1 ? '' : 's'} will lose access to it.`
      : '';
    if (!window.confirm(`Delete “${secret.label}”?${recipeMessage}`)) return;
    setBusyId(secret.id);
    try {
      await deleteSecret(secret.id);
      await loadSecrets();
    } catch (requestError: any) {
      setError(requestError?.response?.data?.message || 'Could not delete this item');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <KitchenLayout title="Vault">
      <main className="min-w-0 flex-1 overflow-y-auto bg-gray-50 p-4 sm:p-10">
        <div className="mx-auto max-w-3xl">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h1 className="text-2xl font-bold text-gray-900">Vault</h1>
            {!isAdding && (
              <button
                onClick={() => { setIsAdding(true); setEditingId(null); setReplacingId(null); setError(''); }}
                className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
              >
                Add item
              </button>
            )}
          </div>
          <p className="mt-2 text-sm text-gray-600">
            Secrets and editable configuration Kitchen provides to your recipes at runtime.
          </p>

          {error && <div className="mt-5 rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</div>}

          {isAdding && (
            <form onSubmit={addSecret} className="mt-6 rounded-xl border border-blue-200 bg-white p-5 shadow-sm">
              <h2 className="font-medium text-gray-900">Add a runtime value</h2>
              <div className="mt-4 grid grid-cols-2 gap-2 rounded-lg bg-gray-100 p-1">
                <button type="button" onClick={() => setNewKind('secret')} className={`rounded-md px-3 py-2 text-sm font-medium ${newKind === 'secret' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600'}`}>Secret</button>
                <button type="button" onClick={() => setNewKind('config')} className={`rounded-md px-3 py-2 text-sm font-medium ${newKind === 'config' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600'}`}>Configuration</button>
              </div>
              <p className="mt-2 text-xs text-gray-500">
                {newKind === 'secret'
                  ? 'Secret values are encrypted, hidden after saving, and protected from appearing in execution records.'
                  : 'Configuration values are encrypted at rest, remain visible and editable here, and are not treated as credentials.'}
              </p>
              <div className="mt-4 space-y-3">
                <div>
                  <label htmlFor="new-secret-name" className="block text-sm font-medium text-gray-700">Name</label>
                  <input
                    id="new-secret-name"
                    value={newLabel}
                    onChange={event => setNewLabel(event.target.value)}
                    maxLength={160}
                    autoFocus
                    placeholder={newKind === 'secret' ? 'For example, OpenAI API key' : 'For example, MAX_CANDIDATES'}
                    className="mt-1 w-full rounded-lg border-gray-300"
                  />
                </div>
                <div>
                  <label htmlFor="new-secret-service" className="block text-sm font-medium text-gray-700">Service or account</label>
                  <input
                    id="new-secret-service"
                    value={newService}
                    onChange={event => setNewService(event.target.value)}
                    maxLength={100}
                    placeholder="Optional"
                    className="mt-1 w-full rounded-lg border-gray-300"
                  />
                </div>
                <div>
                  <label htmlFor="new-secret-value" className="block text-sm font-medium text-gray-700">Value</label>
                  <input
                    id="new-secret-value"
                    type={newKind === 'secret' ? 'password' : 'text'}
                    value={newValue}
                    onChange={event => setNewValue(event.target.value)}
                    autoComplete={newKind === 'secret' ? 'new-password' : 'off'}
                    spellCheck={false}
                    placeholder={newKind === 'secret' ? 'Paste the private value' : 'Enter the configuration value'}
                    className="mt-1 w-full rounded-lg border-gray-300 font-mono"
                  />
                </div>
                <div className="flex gap-2">
                  <button type="submit" disabled={!newLabel.trim() || !newValue.trim() || isCreating} className="rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white disabled:opacity-50">
                    {isCreating ? 'Saving…' : newKind === 'secret' ? 'Save securely' : 'Save configuration'}
                  </button>
                  <button type="button" onClick={cancelAdd} disabled={isCreating} className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 disabled:opacity-50">Cancel</button>
                </div>
              </div>
            </form>
          )}

          <div className={`${isAdding ? 'mt-3' : 'mt-6'} space-y-3`}>
            {isLoading ? (
              <div className="rounded-xl border border-gray-200 bg-white p-6 text-gray-500">Loading your vault…</div>
            ) : secrets.length === 0 ? (
              <div className="rounded-xl border border-gray-200 bg-white p-6">
                <div className="font-medium text-gray-900">Your vault is empty</div>
                <p className="mt-1 text-sm text-gray-600">
                  Add a secret credential or an editable configuration value for your recipes.
                </p>
              </div>
            ) : secrets.map(secret => (
              <section key={secret.id} className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
                {editingId === secret.id ? (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Name</label>
                      <input value={label} onChange={event => setLabel(event.target.value)} className="mt-1 w-full rounded-lg border-gray-300" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Service or account</label>
                      <input value={service} onChange={event => setService(event.target.value)} className="mt-1 w-full rounded-lg border-gray-300" placeholder="Optional" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Value type</label>
                      <select value={editKind} onChange={event => setEditKind(event.target.value as 'secret' | 'config')} className="mt-1 rounded-lg border-gray-300">
                        <option value="secret">Secret</option><option value="config">Configuration</option>
                      </select>
                      <p className="mt-1 text-sm text-gray-500">Use Configuration for account IDs and ordinary settings. Configuration values become visible and may appear in logs. Keep passwords, API keys, and signing tokens as Secret.</p>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => saveDetails(secret.id)} disabled={!label.trim() || busyId === secret.id} className="rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white disabled:opacity-50">Save</button>
                      <button onClick={() => setEditingId(null)} className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700">Cancel</button>
                    </div>
                  </div>
                ) : replacingId === secret.id ? (
                  <div>
                    <div className="font-medium text-gray-900">{secret.kind === 'config' ? 'Edit' : 'Replace'} {secret.label}</div>
                    <p className="mt-1 text-xs text-gray-500">
                      {secret.kind === 'config' ? 'Saving updates the next execution without recompiling the recipe.' : 'The existing value cannot be viewed. Saving replaces it everywhere it is used.'}
                    </p>
                    <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                      <input type={secret.kind === 'secret' ? 'password' : 'text'} value={replacementValue} onChange={event => setReplacementValue(event.target.value)} autoComplete={secret.kind === 'secret' ? 'new-password' : 'off'} spellCheck={false} placeholder={secret.kind === 'secret' ? 'Paste the new private value' : 'Enter the configuration value'} className="min-w-0 flex-1 rounded-lg border-gray-300 font-mono" />
                      <button onClick={() => replaceValue(secret.id)} disabled={!replacementValue.trim() || busyId === secret.id} className="rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white disabled:opacity-50">Save</button>
                      <button onClick={() => { setReplacingId(null); setReplacementValue(''); }} className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700">Cancel</button>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-2">
                        <div className="font-medium text-gray-900">{secret.label}</div>
                        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${secret.kind === 'config' ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-600'}`}>{secret.kind === 'config' ? 'Configuration' : 'Secret'}</span>
                      </div>
                      <div className="mt-1 text-sm text-gray-500">
                        {secret.service || (secret.kind === 'config' ? 'Runtime configuration' : 'Private information')} · Used by {secret.recipeCount || 0} recipe{secret.recipeCount === 1 ? '' : 's'}
                      </div>
                      {secret.kind === 'config' && <div className="mt-1 break-all font-mono text-sm text-gray-700">{secret.value ?? ''}</div>}
                      {!!secret.recipes?.length && (
                        <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-sm">
                          {secret.recipes.map(recipe => (
                            <Link
                              key={recipe.id}
                              to={`/recipes/${recipe.id}/edit`}
                              className="text-blue-600 hover:text-blue-800 hover:underline"
                            >
                              {recipe.name}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
                      <button onClick={() => beginEdit(secret)} className="min-h-11 flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 sm:flex-none">Rename</button>
                      <button onClick={() => { setReplacingId(secret.id); setEditingId(null); setReplacementValue(secret.kind === 'config' ? secret.value || '' : ''); setError(''); }} className="min-h-11 flex-1 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-700 hover:bg-blue-100 sm:flex-none">{secret.kind === 'config' ? 'Edit value' : 'Replace'}</button>
                      <button onClick={() => removeSecret(secret)} disabled={busyId === secret.id} className="min-h-11 flex-1 rounded-lg border border-red-200 px-3 py-2 text-sm text-red-700 hover:bg-red-50 disabled:opacity-50 sm:flex-none">Delete</button>
                    </div>
                  </div>
                )}
              </section>
            ))}
          </div>
        </div>
      </main>
    </KitchenLayout>
  );
};

export default VaultPage;
